import dayjs from "dayjs";
import { DBSchema, IDBPDatabase, IDBPTransaction, openDB } from "idb";

import { parseUploadedUrl } from "@/Services/UploadPhoto";

import {
    StoredPhoto,
    StoredPhotoMeta,
    StoredPhotoSlot,
} from "../PhotoStorage.types";
import { ReadyGate } from "./readyGate";

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

interface PhotoMetaRecord extends StoredPhotoMeta {}

interface PhotoDataRecord {
    id: string;
    base64: string;
}

interface DatabaseSchema extends DBSchema {
    photos_meta: {
        key: string;
        value: PhotoMetaRecord;
        indexes: { "by-date": string };
    };
    photos_data: {
        key: string;
        value: PhotoDataRecord;
    };
    photoSlots: {
        key: string;
        value: StoredPhotoSlot;
        indexes: { "by-point": string };
    };
}

const DB_NAME = "PhotoGalleryDB";
const DB_VERSION = 3;
const META_STORE = "photos_meta";
const DATA_STORE = "photos_data";
const SLOTS_STORE = "photoSlots";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns true for any IDB connection error that warrants a reconnect retry.
 * Covers standard DOMException codes + iOS Safari "UnknownError: Connection
 * to Indexed Database server lost" that fires on low battery / memory pressure.
 */
const isIdbConnectionError = (e: unknown): boolean => {
    if (!(e instanceof Error)) return false;

    const name = e.name ?? "";
    const msg = e.message ?? "";

    return (
        name === "InvalidStateError" ||
        name === "TransactionInactiveError" ||
        // iOS Safari kills IDB under memory/battery pressure
        (name === "UnknownError" &&
            /connection to indexed database server lost/i.test(msg)) ||
        /connection is closing/i.test(msg) ||
        /database.*clos/i.test(msg) ||
        /internal error/i.test(msg)
    );
};

// ---------------------------------------------------------------------------
// Migration helper: copies all records from old "photos" store into the two
// new stores within the same upgrade transaction, then deletes the old store.
// ---------------------------------------------------------------------------

type UpgradeTx = IDBPTransaction<
    DatabaseSchema,
    ("photos_meta" | "photos_data" | "photoSlots")[],
    "versionchange"
>;

async function migrateFromV2(
    db: IDBPDatabase<DatabaseSchema>,
    tx: UpgradeTx,
): Promise<void> {
    // The old store is not in our typed schema, so we access it via the raw
    // IDBDatabase handle to avoid TypeScript complaints.
    const rawDb: IDBDatabase = (db as unknown as { _rawDb: IDBDatabase })
        ._rawDb;

    // idb wraps the native db — reach through to the native handle.
    // If the property name ever changes, the migration simply skips (safe).
    const nativeDb: IDBDatabase | null =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (db as any)._db ?? (db as any).db ?? rawDb ?? null;

    if (!nativeDb?.objectStoreNames.contains("photos")) return;

    const oldStore = (
        tx as unknown as {
            objectStore(name: string): IDBObjectStore;
        }
    ).objectStore("photos");

    await new Promise<void>((resolve, reject) => {
        const req = oldStore.openCursor();

        req.onerror = () => reject(req.error);
        req.onsuccess = () => {
            const cursor = req.result as IDBCursorWithValue | null;

            if (!cursor) {
                resolve();
                return;
            }

            const record = cursor.value as StoredPhoto;
            const { base64, ...meta } = record;

            tx.objectStore(META_STORE).put(meta);
            tx.objectStore(DATA_STORE).put({ id: record.id, base64 });

            cursor.continue();
        };
    });

    nativeDb.deleteObjectStore("photos");
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class PhotoStorageDb {
    private db: IDBPDatabase<DatabaseSchema> | null = null;
    private initPromise: Promise<void> | null = null;
    private readonly gate = new ReadyGate(true);

    async init(): Promise<void> {
        if (this.db) {
            try {
                // Accessing objectStoreNames throws if the connection is dead.
                this.db.objectStoreNames;
                return;
            } catch {
                this.db = null;
                this.initPromise = null;
            }
        }

        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this.openDatabase();
        return this.initPromise;
    }

    /** Resolves when the DB connection is ready. */
    waitUntilReady(): Promise<void> {
        return this.gate.wait();
    }

    private resetConnection(): void {
        // IDB process was killed by the OS (common on iOS low battery).
        // Reset state so the next init() call reopens the connection.
        console.warn(
            "IDB connection terminated by the OS — will reconnect on next operation",
        );
        this.db = null;
        this.initPromise = null;
        this.gate.close();
    }

    private async openDatabase(): Promise<void> {
        try {
            this.db = await openDB<DatabaseSchema>(DB_NAME, DB_VERSION, {
                upgrade(db, oldVersion, _newVersion, tx) {
                    // --- photos_meta ---
                    if (!db.objectStoreNames.contains(META_STORE)) {
                        const store = db.createObjectStore(META_STORE, {
                            keyPath: "id",
                        });
                        store.createIndex("by-date", "createdAt");
                    }

                    // --- photos_data ---
                    if (!db.objectStoreNames.contains(DATA_STORE)) {
                        db.createObjectStore(DATA_STORE, { keyPath: "id" });
                    }

                    // --- photoSlots (unchanged) ---
                    if (!db.objectStoreNames.contains(SLOTS_STORE)) {
                        const store = db.createObjectStore(SLOTS_STORE, {
                            keyPath: "url",
                        });
                        store.createIndex("by-point", "pointId");
                    }

                    // --- migrate data from v2 "photos" store ---
                    if (oldVersion === 2) {
                        migrateFromV2(db, tx as unknown as UpgradeTx).catch(
                            (err) =>
                                console.error(
                                    "IDB v2→v3 migration failed:",
                                    err,
                                ),
                        );
                    }
                },
                terminated: () => this.resetConnection(),
            });
        } finally {
            this.initPromise = null;
            this.gate.open();
        }
    }

    /**
     * Runs `operation` against the open DB.
     * On a connection error it closes the current handle, waits briefly,
     * then re-opens and retries — transparent to the caller.
     */
    private async executeWithRetry<T>(
        operation: (db: IDBPDatabase<DatabaseSchema>) => Promise<T>,
        retries = 2,
    ): Promise<T> {
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                await this.init();
                if (!this.db) throw new Error("Database not initialized");

                return await operation(this.db);
            } catch (error) {
                const isLastAttempt = attempt === retries;

                if (isIdbConnectionError(error) && !isLastAttempt) {
                    console.warn(
                        `IDB connection error on attempt ${
                            attempt + 1
                        }, reconnecting…`,
                        error,
                    );
                    await this.close();
                    // Small back-off: 50 ms on first retry, 150 ms on second.
                    await new Promise((resolve) =>
                        setTimeout(resolve, 50 * (attempt + 1) * 3),
                    );
                    continue;
                }

                throw error;
            }
        }

        throw new Error("Failed to execute database operation after retries");
    }

    // ---------------------------------------------------------------------------
    // Private utils
    // ---------------------------------------------------------------------------

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = (error) => reject(error);
        });
    }

    // ---------------------------------------------------------------------------
    // Photos — write
    // ---------------------------------------------------------------------------

    async savePhoto({
        id,
        file,
        fromSlot,
        skipUpload,
    }: {
        id: string;
        file: File;
        date: string;
        fromSlot: boolean;
        skipUpload?: boolean;
    }): Promise<void> {
        const base64 = await this.fileToBase64(file);

        return this.executeWithRetry(async (db) => {
            const tx = db.transaction([META_STORE, DATA_STORE], "readwrite");

            await Promise.all([
                tx.objectStore(META_STORE).put({
                    id,
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    taskComplete: false,
                    uploadAttempts: 0,
                    uploaded: false,
                    fromSlot,
                    skipUpload,
                    createdAt: dayjs().toISOString(),
                }),
                tx.objectStore(DATA_STORE).put({ id, base64 }),
                tx.done,
            ]);
        });
    }

    // ---------------------------------------------------------------------------
    // Photos — read
    // ---------------------------------------------------------------------------

    async getPhoto(id: string): Promise<string | null> {
        return this.executeWithRetry(async (db) => {
            const record = await db.get(DATA_STORE, id);
            return record?.base64 ?? null;
        });
    }

    /** Returns only metadata — no base64. O(n) in record count, not in bytes. */
    async getAllPhotosMeta(): Promise<StoredPhotoMeta[]> {
        return this.executeWithRetry(async (db) => {
            return db.getAll(META_STORE);
        });
    }

    /** Returns full StoredPhoto objects (meta + base64) for the given ids. */
    async getPhotosByIds(ids: string[]): Promise<StoredPhoto[]> {
        return this.executeWithRetry(async (db) => {
            const [metas, datas] = await Promise.all([
                Promise.all(ids.map((id) => db.get(META_STORE, id))),
                Promise.all(ids.map((id) => db.get(DATA_STORE, id))),
            ]);

            return metas.reduce<StoredPhoto[]>((acc, meta, i) => {
                const data = datas[i];
                if (meta && data) {
                    acc.push({ ...meta, base64: data.base64 });
                }
                return acc;
            }, []);
        });
    }

    async getPhotoInfo(id: string): Promise<StoredPhotoMeta | null> {
        return this.executeWithRetry(async (db) => {
            return (await db.get(META_STORE, id)) ?? null;
        });
    }

    // ---------------------------------------------------------------------------
    // Photos — update
    // ---------------------------------------------------------------------------

    async updatePhotoInfo(
        id: string,
        patch: Partial<StoredPhotoMeta>,
    ): Promise<void> {
        return this.executeWithRetry(async (db) => {
            const tx = db.transaction(META_STORE, "readwrite");
            const store = tx.store;

            const existing = await store.get(id);
            if (!existing) {
                throw new Error(`Photo with id ${id} not found`);
            }

            await store.put({ ...existing, ...patch, id: existing.id });
            await tx.done;
        });
    }

    // ---------------------------------------------------------------------------
    // Photos — delete
    // ---------------------------------------------------------------------------

    async deletePhoto(id: string): Promise<void> {
        return this.executeWithRetry(async (db) => {
            const tx = db.transaction([META_STORE, DATA_STORE], "readwrite");

            await Promise.all([
                tx.objectStore(META_STORE).delete(id),
                tx.objectStore(DATA_STORE).delete(id),
                tx.done,
            ]);
        });
    }

    async deleteAllPhotos(): Promise<void> {
        return this.executeWithRetry(async (db) => {
            const tx = db.transaction([META_STORE, DATA_STORE], "readwrite");

            await Promise.all([
                tx.objectStore(META_STORE).clear(),
                tx.objectStore(DATA_STORE).clear(),
                tx.done,
            ]);
        });
    }

    async deletePhotosByIds(ids: string[]): Promise<void> {
        return this.executeWithRetry(async (db) => {
            const tx = db.transaction([META_STORE, DATA_STORE], "readwrite");
            const meta = tx.objectStore(META_STORE);
            const data = tx.objectStore(DATA_STORE);

            await Promise.all([
                ...ids.map((id) => meta.delete(id)),
                ...ids.map((id) => data.delete(id)),
                tx.done,
            ]);
        });
    }

    // ---------------------------------------------------------------------------
    // Photos — misc
    // ---------------------------------------------------------------------------

    async photoExists(id: string): Promise<boolean> {
        return this.executeWithRetry(async (db) => {
            // count is faster than get — doesn't deserialise the record
            return (await db.count(META_STORE, id)) > 0;
        });
    }

    // ---------------------------------------------------------------------------
    // Slots store
    // ---------------------------------------------------------------------------

    async getSlotAndDelete(pointId: string): Promise<StoredPhotoSlot | null> {
        return this.executeWithRetry(async (db) => {
            const tx = db.transaction(SLOTS_STORE, "readwrite");
            const store = tx.objectStore(SLOTS_STORE);
            const index = store.index("by-point");

            const cursor = await index.openCursor(pointId);

            if (!cursor) {
                await tx.done;
                return null;
            }

            const slot = cursor.value;
            await cursor.delete();
            await tx.done;

            return slot;
        });
    }

    async createSlot({ url }: { url: string }): Promise<void> {
        return this.executeWithRetry(async (db) => {
            const parseData = parseUploadedUrl(url);
            if (!parseData) return;

            const { pointId, middlePath, filename } = parseData;
            await db.put(SLOTS_STORE, { url, pointId, filename, middlePath });
        });
    }

    async deleteSlot(url: string): Promise<void> {
        return this.executeWithRetry(async (db) => {
            await db.delete(SLOTS_STORE, url);
        });
    }

    // ---------------------------------------------------------------------------
    // Connection management
    // ---------------------------------------------------------------------------

    async close(): Promise<void> {
        if (this.db) {
            try {
                this.db.close();
            } catch (error) {
                console.warn("Error closing database:", error);
            } finally {
                this.db = null;
                this.initPromise = null;
                this.gate.close();
            }
        }
    }

    openGate(): void {
        this.gate.open();
    }

    closeGate(): void {
        this.gate.close();
    }

    // ---------------------------------------------------------------------------
    // Kind
    // ---------------------------------------------------------------------------

    async getKind(): Promise<"idb"> {
        return "idb";
    }
}

export { PhotoStorageDb };
